import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { fetchSessions } from '../services/api';
import { formatImageUrl } from '../utils/imageUtils';
import {
  CalendarIcon,
  MapPinIcon,
  UserIcon,
  ClockIcon,
  TagIcon
} from '@heroicons/react/24/outline';

// Use the Session interface from the API service
interface Session {
  id: string;
  title: string;
  description: string;
  type: string;
  speakers: { 
    id: string; 
    name: string; 
    display_order: number;
    image?: string;
    company?: string;
  }[];
  startTime: string;
  endTime: string;
  room: string;
  status: string;
}

interface GroupedSessions {
  [date: string]: Session[];
}

const Schedule: React.FC = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

  const TIME_ZONE = 'Europe/Vienna';

  const getViennaDateKey = (isoString: string): string => {
    return isoString.split('T')[0];
  };

  useEffect(() => {
    const loadSessions = async () => {
      try {
        const response = await fetchSessions();
        const sortedSessions = response.sessions.sort(
          (a, b) => a.startTime.localeCompare(b.startTime)
        );
        setSessions(sortedSessions);
        
        // Set the first date as selected
        if (sortedSessions.length > 0) {
          const firstDate = getViennaDateKey(sortedSessions[0].startTime);
          setSelectedDate(firstDate);
        }
        
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load schedule');
        setLoading(false);
      }
    };

    loadSessions();
  }, []);

  const groupSessionsByDate = (sessions: Session[]): GroupedSessions => {
    return sessions.reduce((groups, session) => {
      const date = getViennaDateKey(session.startTime);
      return {
        ...groups,
        [date]: [...(groups[date] || []), session]
      };
    }, {} as GroupedSessions);
  };

  const formatDate = (dateString: string): string => {
    const [y, m, d] = dateString.split('-').map(Number);
    const dateUTC = new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
    return new Intl.DateTimeFormat('en-US', {
      timeZone: TIME_ZONE,
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(dateUTC);
  };

  const formatTime = (timeString: string): string => {
    const timePart = (timeString.split('T')[1] || '00:00:00');
    const [hStr, mStr] = timePart.split(':');
    const h = parseInt(hStr || '0', 10);
    const m = parseInt(mStr || '0', 10);
    const period = h >= 12 ? 'PM' : 'AM';
    const hh12 = ((h + 11) % 12) + 1;
    return `${hh12}:${m.toString().padStart(2, '0')} ${period}`;
  };

  const getSessionTypeColor = (type: string): string => {
    const colors: { [key: string]: string } = {
      keynote: 'bg-purple-100 text-purple-800',
      workshop: 'bg-blue-100 text-blue-800',
      presentation: 'bg-green-100 text-green-800',
      panel: 'bg-orange-100 text-orange-800',
      default: 'bg-gray-100 text-gray-800'
    };
    return colors[type.toLowerCase()] || colors.default;
  };

  const truncateText = (text: string, wordLimit: number = 130): { truncated: string; isLong: boolean } => {
    const words = text.split(' ');
    if (words.length <= wordLimit) {
      return { truncated: text, isLong: false };
    }
    return { 
      truncated: words.slice(0, wordLimit).join(' ') + '...', 
      isLong: true 
    };
  };

  const toggleDescription = (sessionId: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        newSet.delete(sessionId);
      } else {
        newSet.add(sessionId);
      }
      return newSet;
    });
  };

  const groupedSessions = groupSessionsByDate(sessions);
  const dates = Object.keys(groupedSessions).sort();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex justify-center items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading schedule</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Conference Schedule</h1>
          <p className="text-xl text-gray-600">
            Explore our lineup of exciting sessions and plan your conference experience
          </p>
          <p className="text-sm text-gray-500 mt-2">All times are shown in Vienna (CET/CEST)</p>
        </motion.div>

        {/* Date Navigation */}
        {dates.length > 0 ? (
          <div className="mb-8">
            <div className="flex flex-wrap gap-4 justify-center">
              {dates.map((date) => (
                <button
                  key={date}
                  onClick={() => setSelectedDate(date)}
                  className={`px-6 py-3 rounded-full font-medium transition-all duration-200 ${
                    selectedDate === date
                      ? 'bg-[#00529B] text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-gray-50 shadow'
                  }`}
                >
                  {formatDate(date)}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {/* Empty State */}
        {sessions.length === 0 && !loading && !error ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center justify-center py-16 px-4 text-center"
          >
            <CalendarIcon className="w-20 h-20 text-gray-300 mb-6" />
            <h2 className="text-2xl font-semibold text-gray-700 mb-3">No Schedule Items Yet</h2>
            <p className="text-gray-500 max-w-md mb-6">
              The conference schedule is currently being finalized. Please check back later for updates on sessions, workshops, and keynotes.
            </p>
          </motion.div>
        ) : null}

        {/* Sessions for Selected Date */}
        {selectedDate && sessions.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="space-y-6"
          >
            {groupedSessions[selectedDate]?.length > 0 ? (
              groupedSessions[selectedDate].map((session) => (
                <motion.div
                  key={session.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getSessionTypeColor(session.type)}`}>
                            {session.type}
                          </span>
                          <span className="text-gray-500 text-sm flex items-center">
                            <ClockIcon className="w-4 h-4 mr-1" />
                            {formatTime(session.startTime)} - {formatTime(session.endTime)}
                          </span>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{session.title}</h3>
                        <div className="text-gray-600 mb-4">
                          {(() => {
                            const { truncated, isLong } = truncateText(session.description);
                            const isExpanded = expandedDescriptions.has(session.id);
                            const displayText = isExpanded ? session.description : truncated;
                            
                            return (
                              <>
                                <p className="whitespace-pre-wrap">{displayText}</p>
                                {isLong && (
                                  <button
                                    onClick={() => toggleDescription(session.id)}
                                    className="text-[#00529B] hover:text-[#003D74] font-medium text-sm mt-2 transition-colors duration-200"
                                  >
                                    {isExpanded ? 'Read less' : 'Read more'}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                        {/* Enhanced Speaker Display */}
                        {session.speakers && session.speakers.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                              <UserIcon className="w-4 h-4 mr-1" />
                              {session.speakers.length === 1 ? 'Speaker' : 'Speakers'}
                            </h4>
                            <div className="flex flex-wrap gap-3">
                              {session.speakers
                                .sort((a, b) => a.display_order - b.display_order)
                                .map(speaker => (
                                  <div key={speaker.id} className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3 min-w-0">
                                    <div className="flex-shrink-0">
                                      <img
                                        src={formatImageUrl(speaker.image, "/placeholder-speaker.svg")}
                                        alt={speaker.name}
                                        className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm"
                                      />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {speaker.name}
                                      </p>
                                      {speaker.company && (
                                        <p className="text-xs text-gray-500 truncate">
                                          {speaker.company}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                          <div className="flex items-center">
                            <MapPinIcon className="w-4 h-4 mr-1" />
                            <span>{session.room}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex flex-col items-center justify-center py-12 px-4 text-center bg-white rounded-xl shadow"
              >
                <CalendarIcon className="w-16 h-16 text-gray-300 mb-4" />
                <h2 className="text-xl font-semibold text-gray-700 mb-2">No Events on This Day</h2>
                <p className="text-gray-500">
                  There are no scheduled sessions for {formatDate(selectedDate)}. Please select another date.
                </p>
              </motion.div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Schedule;

